import React, { useEffect, useState, createContext, useContext, PropsWithChildren, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumb, LoginAuth, SiteRoute } from './DashboardClasses';
import { joinUrls } from '@payfront/components/Util';
import API from './API';
import { useTranslation } from 'react-i18next';
import UserAccess from './UserAccess';

var TitleContext = createContext<{ setTitle: (title: string)=>void }>({ setTitle: (v: string)=>null });
var useTitle = () => useContext(TitleContext);

interface TitleProps extends PropsWithChildren{
	default?: string,
	suffix?: string,
}

var TitleProvider = (props: TitleProps) => {
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string }[]>([]);
	var location = useLocation();

	useEffect(()=>{
		var suffix = props.suffix || 'Payfront';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		if(!title || !title.title){
			document.title = suffix;
		}else{
			document.title = `${title.title} - ${suffix}`;
		}
	}, [location, titleHistory]);

	var changeTitle = (title: string)=>{
		if(!location.key) return;
		var hs = [...titleHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title });
		setTitleHistory(hs);
	}

	return (
		<TitleContext.Provider value={{ setTitle: changeTitle }}>
			{props.children}
		</TitleContext.Provider>
	)
}

var UserContext = createContext<{
	user: LoginAuth,
	loaded: boolean,
	loadedError: string,
	admin: boolean,
	live: boolean,
	setLive: (live: boolean)=>void,
	active_client: number,
	hasClientAccess: (a: number)=>boolean,
	setActiveClient: (client_id: number)=>void,
}>(null);
var useUser = ()=>useContext(UserContext);
var UserProvider = (props: PropsWithChildren)=>{
	var { t } = useTranslation();
	var [loaded, setLoaded] = useState<boolean>(false);
	var [loadedError, setLoadedError] = useState<string>(null);
	var [user, setUser] = useState<LoginAuth>(API.getLoginAuth());
	var [clientAccess, setClientAccess] = useState<{ client_id: number, admin: boolean, access: number[] }[]>([]);
	var [activeClient, setActiveClient] = useState<number>(null);
	var [live, setLive] = useState<boolean>(true);

	useEffect(()=>{
		var userListener = ()=>{
			setUser(API.getLoginAuth());
		}

		window.addEventListener('PYF-USER-AUTH', userListener)
		return ()=>{
			window.removeEventListener('PYF-USER-AUTH', userListener);
		}
	}, []);

	useEffect(()=>{
		if(!user) return;
		var client = API.getActiveClient();
		if(!client){
			setLoaded(true);
			setLoadedError(null);
		}else{
			changeActiveClient(client);
		}
	}, [user]);

	useEffect(()=>{
		var lclive = localStorage.getItem('live-mode');
		if(lclive==='0') setLive(false);
		else setLive(true);
	}, []);

	var changeActiveClient = (client_id: number)=>{
		setActiveClient(client_id);
		setLoadedError(null);
		if(client_id){
			setLoaded(false);
			localStorage.setItem('active-client', client_id.toString());
			if(!user.admin && !clientAccess.find(a=>a.client_id===client_id)){
				API.getUserClientAccess(client_id).then(res=>{
					if(res.error) return setLoadedError(res.message);
					setClientAccess([
						...clientAccess,
						{
							client_id,
							admin: res.data.admin,
							access: res.data.permissions,
						}
					]);
				}).catch(err=>{
					setLoadedError(t('common.unexpected_error', { code: 'LCL-CHACLT-1' }))
				}).finally(()=>{
					setLoaded(true);
				})
			}else{
				setLoaded(true);
			}
		}else{
			localStorage.removeItem('active-client');
		}
	}

	var hasClientAccess = (access_id: number)=>{
		if(!activeClient || activeClient==-1) return true;
		if(user.admin) return true;
		var clacc = clientAccess.find(a=>a.client_id===activeClient);
		if(!clacc) return false;
		return clacc.admin || clacc.access.indexOf(access_id)!=-1;
	}

	var updateLive = (l: boolean)=>{
		setLive(l);
		localStorage.setItem('live-mode', l ? '1' : '0');
		setTimeout(()=>{
			window.location.reload();
		}, 100);
	}

	return <UserContext.Provider value={{
		user,
		loaded,
		loadedError,
		admin: activeClient===-1,
		active_client: activeClient,
		live: activeClient===-1 ? true : (hasClientAccess(UserAccess.DEVELOPER.SANDBOX) ? live : true),
		setLive: updateLive,
		hasClientAccess,
		setActiveClient: changeActiveClient,
	}}>
		{props.children}
	</UserContext.Provider>
}

var BreadcrumbsContext = createContext<{
	breadcrumbs: Breadcrumb[]
}>(null);
var useBreadcrumbs = ()=>useContext(BreadcrumbsContext);
var BreadcrumbsProvider = (props: PropsWithChildren & { routes: SiteRoute[] })=>{
	var location = useLocation();
	var getBreadcumbUrl = (params: { [x: string]: any }, url: string)=>{
		for(var i in params){
			url = url.replace(new RegExp(`:${i}`, 'gi'), params[i]);
		}
		return url;
	}

	var getBreadcrumb = (location: string, parent: string, routes: SiteRoute[]) : Breadcrumb[]=>{
		var bread : Breadcrumb[] = [];
		for(var i of routes){
			var path = joinUrls(parent, i.path);
			var paths = path.split('/').filter(a=>(!!a && a.length>0));
			var reg = new RegExp(`/${paths.map(p=>{
				if(p.startsWith(':')){
					return `(?<${p.substring(1)}>[A-z0-9_]+)`
				}else{
					return p;
				}
			}).join('/')}/?`)
			var reg_res = reg.exec(location);
			if(reg_res){
				bread.push({
					text: typeof i.title==='string' ? i.title : i.title(reg_res.groups),
					url: getBreadcumbUrl(reg_res.groups, path),
					iconName: i.iconName,
				})
				if(i.children){
					bread.push(...getBreadcrumb(location, path, i.children));
				}
				break;
			}
		}
		return bread;
	}

	
	var breadcrumbs = useMemo(()=>{
		if(props.routes){
			return getBreadcrumb(location.pathname, '/', props.routes);
		}else return [];
	}, [location?.pathname]);

	return <BreadcrumbsContext.Provider value={{
		breadcrumbs,
	}}>
		{props.children}
	</BreadcrumbsContext.Provider>
}

export {
	TitleProvider,
	useTitle,

	UserProvider,
	useUser,

	BreadcrumbsProvider,
	useBreadcrumbs,
}